import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { superAdminAPI } from '../../services/api';
import {
  FilterSelect, ReportFilterBar, EMPTY_REPORT_FILTERS, filterReportRows, uniqueSorted,
} from '../../components/SuperAdmin/ListTools';
import { formatExportDate } from '../../utils/exportData';
import {
  HiOutlineExclamation,
  HiOutlineRefresh,
  HiOutlineTicket,
  HiOutlineUser,
} from 'react-icons/hi';
import { ReportGuide } from '../../components/SuperAdmin/ReportGuide';

const formatCreated = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const PRIORITY_LABELS = {
  urgent: 'Urgent',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

const PRIORITY_COLORS = {
  urgent: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-blue-100 text-blue-700',
  low: 'bg-gray-100 text-gray-600',
};

const PRIORITIES = ['urgent', 'high', 'medium', 'low'];

const formatOverdue = (m) => {
  if (!m || m <= 0) return '—';
  if (m < 60) return `${m}m`;
  if (m < 1440) return `${Math.floor(m / 60)}h ${m % 60}m`;
  return `${Math.floor(m / 1440)}d ${Math.floor((m % 1440) / 60)}h`;
};

const OverdueTicketReport = () => {
  const [priority, setPriority] = useState('');

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['overdue-tickets', priority],
    queryFn: () =>
      superAdminAPI.getOverdueTickets(priority ? { priority } : undefined),
    refetchInterval: 120000,
  });

  // Axios → res.data (server body) → body.data
  const body = data?.data?.data || {};
  const stats = body.stats || {};
  const byPriority = body.byPriority || {};
  const allRows = Array.isArray(body.rows) ? body.rows : [];
  const [filters, setFilters] = useState({ ...EMPTY_REPORT_FILTERS, assignee: '', overdueType: '' });
  const assigneeOf = (r) => r.supportAdminName || r.assigneeName || 'Unassigned';
  const rows = filterReportRows(allRows, filters, {
    searchOf: (r) => [r.ticketNumber, r.subject, r.organizationName, assigneeOf(r)],
  }).filter((r) =>
    (!filters.assignee || assigneeOf(r) === filters.assignee)
    && (!filters.overdueType
      || (filters.overdueType === 'response' && r.responseOverdue)
      || (filters.overdueType === 'resolution' && r.resolutionOverdue)
      || (filters.overdueType === 'both' && r.responseOverdue && r.resolutionOverdue)));

  const overdueLevel = (m) =>
    m >= 1440 * 3 ? 'bg-red-100 text-red-700'
    : m >= 1440 ? 'bg-red-50 text-red-600'
    : m >= 240 ? 'bg-orange-100 text-orange-700'
    : 'bg-yellow-100 text-yellow-700';

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex-1 basis-80">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <HiOutlineExclamation className="w-7 h-7 text-red-500" />
            Overdue Tickets
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Open tickets that are late, so the team can act on them now
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500/40"
          >
            <option value="">All priorities</option>
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>
            ))}
          </select>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium
                       hover:bg-primary-600 shadow-lg shadow-primary-500/25 disabled:opacity-50"
          >
            <HiOutlineRefresh className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <ReportGuide
        id="overdue-tickets"
        summary="Your to-do list of late tickets. The longest-waiting tickets need attention first."
        points={[
          ["Response overdue", "Customer has not had a first reply in time."],
          ["Resolution overdue", "Ticket was not solved in time."],
          ["Both overdue", "Late on both the reply and the fix."],
          ["Total overdue", "How long past the deadline the ticket is."],
        ]}
      />

      {/* Stat tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Overdue Tickets</p>
          <p className={`text-3xl font-bold mt-2 ${stats.totalOverdue > 0 ? 'text-red-500' : 'text-green-500'}`}>
            {stats.totalOverdue ?? 0}
          </p>
          <p className="text-xs text-gray-400 mt-1">Open tickets past their deadline</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Response Overdue</p>
          <p className="text-3xl font-bold text-orange-500 mt-2">{stats.responseOverdue ?? 0}</p>
          <p className="text-xs text-gray-400 mt-1">No first response received yet</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Resolution Overdue</p>
          <p className="text-3xl font-bold text-amber-500 mt-2">{stats.resolutionOverdue ?? 0}</p>
          <p className="text-xs text-gray-400 mt-1">Still unresolved past deadline</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Both Overdue</p>
          <p className="text-3xl font-bold text-red-500 mt-2">{stats.bothOverdue ?? 0}</p>
          <p className="text-xs text-gray-400 mt-1">Late on both reply and fix</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Worst Overdue</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{formatOverdue(stats.worstOverdueMinutes)}</p>
          <p className="text-xs text-gray-400 mt-1">Longest overdue ticket</p>
        </div>
      </div>

      {/* By priority chips */}
      {Object.keys(byPriority).length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Overdue by Priority</p>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(byPriority).map(([key, count]) => (
              <span
                key={key}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold ${PRIORITY_COLORS[key] || 'bg-gray-100 text-gray-600'}`}
              >
                {PRIORITY_LABELS[key] || key}: {count}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Overdue tickets table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="text-sm font-semibold text-gray-700">Overdue Tickets</h2>
          <span className="text-xs text-gray-400">
            {rows.length === allRows.length ? `${rows.length} shown` : `${rows.length} of ${allRows.length} shown`}
          </span>
        </div>

        {allRows.length > 0 && (
          <ReportFilterBar
            filters={filters}
            onChange={setFilters}
            showPriority={false}
            searchPlaceholder="Search ticket #, subject, organization, assignee…"
            organizations={uniqueSorted(allRows.map((r) => r.organizationName))}
            extraActive={!!(filters.assignee || filters.overdueType)}
            onResetExtra={() => setFilters((f) => ({ ...f, assignee: '', overdueType: '' }))}
            exportProps={{
              fileName: `overdue-tickets${priority ? `-${priority}` : ''}`,
              getRows: () => rows,
              columns: [
                { header: 'Ticket #', value: (r) => r.ticketNumber },
                { header: 'Subject', value: (r) => r.subject },
                { header: 'Organization', value: (r) => r.organizationName },
                { header: 'Priority', value: (r) => PRIORITY_LABELS[r.priority] || r.priority },
                { header: 'Assignee', value: assigneeOf },
                { header: 'Created', value: (r) => formatExportDate(r.createdAt) },
                { header: 'Response Overdue', value: (r) => (r.responseOverdue ? formatOverdue(r.responseOverdueMinutes) : 'OK') },
                { header: 'Resolution Overdue', value: (r) => (r.resolutionOverdue ? formatOverdue(r.resolutionOverdueMinutes) : 'OK') },
                { header: 'Total Overdue (min)', value: (r) => r.overdueMinutes },
                { header: 'Total Overdue', value: (r) => formatOverdue(r.overdueMinutes) },
              ],
            }}
          >
            <FilterSelect
              value={filters.assignee}
              onChange={(v) => setFilters((f) => ({ ...f, assignee: v }))}
              allLabel="All Assignees"
              options={uniqueSorted(allRows.map(assigneeOf)).map((a) => [a, a])}
            />
            <FilterSelect
              value={filters.overdueType}
              onChange={(v) => setFilters((f) => ({ ...f, overdueType: v }))}
              allLabel="Any Overdue Type"
              options={[['response', 'Response overdue'], ['resolution', 'Resolution overdue'], ['both', 'Both overdue']]}
            />
          </ReportFilterBar>
        )}

        {isLoading ? (
          <div className="p-8 text-center text-gray-400 text-sm">Loading overdue ticket report…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-400">
            {allRows.length > 0
              ? 'No tickets match these filters'
              : 'No tickets have exceeded their response or resolution time — all clear ✓'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3">Ticket</th>
                  <th className="px-4 py-3">Organization</th>
                  <th className="px-4 py-3">Priority</th>
                  <th className="px-4 py-3">Assignee</th>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3">Response</th>
                  <th className="px-4 py-3">Resolution</th>
                  <th className="px-4 py-3">Total Overdue</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row) => (
                  <tr key={row.ticketId || row.ticketNumber} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <HiOutlineTicket className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-gray-900">{row.ticketNumber}</p>
                          <p className="text-xs text-gray-400 max-w-[220px] truncate" title={row.subject}>
                            {row.subject}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{row.organizationName || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${PRIORITY_COLORS[row.priority] || 'bg-gray-100 text-gray-600'}`}>
                        {PRIORITY_LABELS[row.priority] || row.priority}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {row.supportAdminName ? (
                        <span className="inline-flex items-center gap-1.5 text-gray-700 text-xs">
                          <HiOutlineUser className="w-3.5 h-3.5 text-gray-400" />
                          <span className="font-medium">{row.supportAdminName}</span>
                          <span className="px-1.5 py-0.5 rounded text-[10px] bg-primary-50 text-primary-600 font-medium">Support</span>
                        </span>
                      ) : row.assigneeName ? (
                        <span className="inline-flex items-center gap-1.5 text-gray-600 text-xs">
                          <HiOutlineUser className="w-3.5 h-3.5 text-gray-400" />
                          {row.assigneeName}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">Unassigned</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{formatCreated(row.createdAt)}</td>
                    <td className="px-4 py-3">
                      {row.responseOverdue ? (
                        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                          {formatOverdue(row.responseOverdueMinutes)} late
                        </span>
                      ) : (
                        <span className="text-xs text-green-600 font-medium">OK</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {row.resolutionOverdue ? (
                        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                          {formatOverdue(row.resolutionOverdueMinutes)} late
                        </span>
                      ) : (
                        <span className="text-xs text-green-600 font-medium">OK</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${overdueLevel(row.overdueMinutes)}`}>
                        {formatOverdue(row.overdueMinutes)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default OverdueTicketReport;